import React, { useMemo, useRef, useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';

import { useAppContext } from '@prisjakt/ui-app-context';

import { useActiveCampaignByProductId } from 'src/hooks/active-campaign-by-product-id';

import { CompactListLayout } from 'src/areas/product-collection/components/products/layout-compact-list';
import { ListLayout } from 'src/areas/product-collection/components/products/layout-list';
import { GridLayout } from 'src/areas/product-collection/components/products/layout-grid';
import Pagination from 'src/areas/product-collection/components/productOffersContainer/pagination';
import { ResultFilters } from '../../filters';
import { SearchResultsProductsHeader } from '../../panel-header';
import { NoSearchResults } from 'src/components/no-search-results';

import { useLocalStorage } from 'src/hooks/use-localstorage';

import { MAX_RESULTS } from '../../../constants';
import { NAV_HEIGHT } from 'src/components/sticky-tab-nav/constants';

import {
  trackKeywordSearchPageOfferClick,
  trackKeywordSearchPageProductClick,
} from '../../../tracking';

import {
  Product,
  Raw,
  Offer,
  SearchPageInfoKwlp as SearchPageInfo,
  SortInfo,
  SearchResultFiltersKwlp as SearchResultFilters,
} from 'src/types';
import { LayoutMode } from 'src/components/layout-buttons/types';
import { TabAnchor } from '../../navigation/types';
import { Filter } from '../types';

import * as S from '../styles';
import {
  useTrailingSkyscraperExperiment,
  useSearchUnfeaturedClickout,
} from 'src/hooks/experiments';

type NewFacets = SearchResultFilters | undefined;

type Props = {
  facets?: NewFacets;
  pageInfo?: SearchPageInfo | null;
  products: (
    | Omit<Product, 'classicFullPath' | 'conditions' | 'media'>
    | Omit<Raw, 'store'>
    | Omit<
        Offer,
        | 'stockStatus'
        | 'id'
        | 'store'
        | 'media'
        | 'name'
        | 'externalUri'
        | 'offerPrice'
      >
    | null
  )[];
  productsCount: number;
  sort?: SortInfo | null;
  query: string;
  isWide?: boolean;
};

const ADS_LAYOUT = {
  [LayoutMode.GRID]: {
    panorama_extra: { show: false, row: 2 },
    dubbelpanorama1: { show: true, row: 2 },
    dubbelpanorama2: { show: true, row: 8 },
  },
  [LayoutMode.LIST]: {
    panorama_extra: { show: false, row: 2 },
    dubbelpanorama1: { show: true, row: 3 },
    dubbelpanorama2: { show: true, row: 8 },
  },
  [LayoutMode.COMPACT_LIST]: {
    panorama_extra: { show: false, row: 2 },
    dubbelpanorama1: { show: true, row: 3 },
    dubbelpanorama2: { show: true, row: 15 },
  },
};

export const ProductResults = (props: Props) => {
  const { facets, pageInfo, products, productsCount, query, isWide } = props;

  const { t } = useAppContext();
  const dispatch = useDispatch();
  const productsContainer = useRef<HTMLDivElement>(null);

  const [activeLayout, setLayoutState] = useState<LayoutMode>(
    LayoutMode.COMPACT_LIST
  );

  const [persistentLayout, setPersistentLayout] = useLocalStorage(
    'pj:searchResultsLayout',
    LayoutMode.COMPACT_LIST
  );
  const trailingSkyscraperVariant = useTrailingSkyscraperExperiment();
  const unfeaturedClickout = useSearchUnfeaturedClickout();

  useEffect(() => {
    if (activeLayout) {
      dispatch({
        type: 'ADS_VIEW_UPDATE',
        view: `keywordlandingpage:layout:${activeLayout}`,
      });
    }
  }, [activeLayout]);

  useEffect(() => {
    // Change layout to the last used mode saved in localStorage
    if (persistentLayout) {
      setLayoutState(persistentLayout);
    }
  }, []);

  const productIds = useMemo(
    () =>
      products?.length
        ? (products
            .map(product => (product && 'id' in product ? product.id : null))
            .filter(id => id) as number[])
        : [],
    [products]
  );
  const productsWithActiveCampaign = useActiveCampaignByProductId(
    productIds,
    true
  );

  const setLayout = (layout: LayoutMode) => {
    setLayoutState(layout);
    setPersistentLayout(layout);
  };

  const handlePaginationClick = () => {
    if (!IS_BROWSER || !productsContainer.current?.offsetTop) {
      return;
    }

    if (trailingSkyscraperVariant === 'trailing and refreshing skyscraper') {
      dispatch({
        type: 'ADS_VIEW_UPDATE',
        view: `keywordlandingpage:layout:${activeLayout}`,
      });
    }

    window.scrollTo({
      top: productsContainer.current?.offsetTop - NAV_HEIGHT,
      behavior: 'smooth',
    });
  };

  const onProductClickHandler = (
    id: string | number,
    name?: string,
    type?: string
  ) => {
    if (type === 'Offer') {
      const clickedProduct = products.find(p => {
        if (p?.__typename === 'Offer' && (p as Offer)?.id === id) {
          return true;
        }
        return false;
      }) as Offer | undefined;

      // TODO: fix this hack!
      trackKeywordSearchPageOfferClick(name, (clickedProduct as any).offerId);
    }
    if (type === 'Product') trackKeywordSearchPageProductClick(name, id);
  };

  const newFacets = facets as NewFacets;
  const newFilters = [
    {
      id: 'category_id',
      type: 'SearchFacetList',
      title: t('search', 'facets.title-category_id'),
      options: newFacets?.category?.map(
        f =>
          f && {
            name: f.name,
            value: f.name,
            id: f.id,
            count: f.productCount,
          }
      ),
    },
    {
      id: 'brand_id',
      type: 'SearchFacetList',
      title: t('search', 'facets.title-brand_id'),
      options: newFacets?.brand?.map(
        f =>
          f && {
            name: f.name,
            value: f.name,
            id: f.id,
            count: f.productCount,
          }
      ),
    },
  ].filter(Boolean) as Filter[];

  const filters = newFilters;
  const totalProducts = pageInfo?.total || 0;

  return (
    <div ref={productsContainer}>
      <S.Section id={TabAnchor.PRODUCTS}>
        <SearchResultsProductsHeader
          activeLayout={activeLayout}
          productsCount={productsCount}
          setLayout={setLayout}
        />

        <S.FilterWrapper>
          {filters ? (
            <ResultFilters
              productsCount={productsCount}
              filters={filters}
              query={query}
            />
          ) : null}
        </S.FilterWrapper>

        {!products.length ? (
          <NoSearchResults />
        ) : (
          <>
            {activeLayout === LayoutMode.GRID && (
              <GridLayout
                productsWithActiveCampaign={productsWithActiveCampaign}
                products={
                  products.filter(
                    p =>
                      p?.__typename === 'Product' ||
                      p?.__typename === 'Raw' ||
                      p?.__typename === 'Offer'
                  ) as (Product | Raw)[]
                }
                adslayoutConfig={ADS_LAYOUT}
                onClick={onProductClickHandler}
                isWide={isWide}
                unfeaturedClickout={unfeaturedClickout}
              />
            )}
            {activeLayout === LayoutMode.LIST && (
              <ListLayout
                productsWithActiveCampaign={productsWithActiveCampaign}
                products={
                  products.filter(
                    p =>
                      p?.__typename === 'Product' ||
                      p?.__typename === 'Raw' ||
                      p?.__typename === 'Offer'
                  ) as (Product | Raw)[]
                }
                adslayoutConfig={ADS_LAYOUT}
                onClick={onProductClickHandler}
                unfeaturedClickout={unfeaturedClickout}
              />
            )}

            {activeLayout === LayoutMode.COMPACT_LIST && (
              <CompactListLayout
                productsWithActiveCampaign={productsWithActiveCampaign}
                products={
                  products.filter(
                    p =>
                      p?.__typename === 'Product' ||
                      p?.__typename === 'Raw' ||
                      p?.__typename === 'Offer'
                  ) as (Product | Raw)[]
                }
                adslayoutConfig={ADS_LAYOUT}
                onClick={onProductClickHandler}
                unfeaturedClickout={unfeaturedClickout}
              />
            )}

            {pageInfo?.limit && productsCount > pageInfo?.limit && (
              <Pagination
                pageInfo={pageInfo}
                total={
                  totalProducts >= MAX_RESULTS ? MAX_RESULTS : totalProducts
                }
                onClick={handlePaginationClick}
              />
            )}
          </>
        )}
      </S.Section>
    </div>
  );
};
